import classNames from 'classnames'
import { ArrowLeft, Moon, Sun } from 'lucide-react'
import React, { ReactNode, useEffect, useMemo, useState } from 'react'

import { DEFAULT_LANGUAGE, t } from '../../../common/i18n.js'
import { Button, ButtonProps } from '../../components/button/button.js'
import { MultiSelect, MultiSelectProps } from '../../components/forms/select/select.js'
import { TextField, TextFieldProps } from '../../components/forms/text-field/text-field.js'
import { ToggleProps } from '../../components/forms/toggle/toggle.js'
import { GridColumn } from '../../components/grid/grid-column.js'
import { Grid } from '../../components/grid/grid.js'
import { CropModal, CropModalProps } from '../../components/modal/cropper-modal/crop-modal.js'
import { CustomLineType, SignatureProps } from '../../components/signature/signature.js'
import { Option } from '../../components/types/common.js'
import { State } from '../../types.js'
import { SignatureFormData } from '../../utils/signature-form-utils.js'
import './signature-creation.scss'
import { InfoRow, SignatureInfoRows } from './signature-info-rows.js'
import { SignaturePreview } from './signature-preview.js'

type LogoTag = 'play' | 'nope' | 'alliance'

const logoOptions: Option<LogoTag>[] = [
    { value: 'play', label: 'gotoAndPlay' },
    { value: 'nope', label: 'NOPE' },
    { value: 'alliance', label: 'Alliance' },
]
export interface SignatureCreationProps {
    children: ReactNode
    className?: string
    signature: SignatureProps
    navigateBack?: () => void
    backButtonProps: ButtonProps
    titlePrefix: string
    titlePlaySuffix: string
    titleNopeSuffix: string
    requiredFieldsText: string
    fields: {
        name: TextFieldProps
        position: TextFieldProps
        email: TextFieldProps
    }
    customRow: {
        titleFieldProps: TextFieldProps
        contentFieldProps: TextFieldProps
        linkFieldProps: TextFieldProps
    }
    buttons: {
        save: ButtonProps
        cancel: ButtonProps
        addRow: ButtonProps
        removeRow: ButtonProps
    }
    image: {
        uploadLabel: string
        selectButtonProps: ButtonProps
        removeButtonProps: ButtonProps
        noImageSelectedText: string
    }
    previewTitle: string
    toggleProps: ToggleProps
    multiSelectProps: MultiSelectProps
    cropModalProps: CropModalProps
    mode?: 'create' | 'edit'
    initialData?: SignatureFormData
    state?: State
    onSave?: (formData: SignatureFormData) => void | Promise<void>
    onCancel?: () => void
    isLoading?: boolean
    signatureId?: string
    company?: 'play' | 'nope'
    isLinkLabel?: string
}

export const SignatureCreation = (props: SignatureCreationProps) => {
    const mode = props.mode || 'create'
    const [name, setName] = useState('')
    const [position, setPosition] = useState('')
    const [email, setEmail] = useState('')
    const [tags, setTags] = useState<LogoTag[]>([])
    const [isCropModalOpen, setIsCropModalOpen] = useState(false)
    const [cropSrc, setCropSrc] = useState<string | null>(null)
    const [croppedImageUrl, setCroppedImageUrl] = useState<string | undefined>(undefined)
    const [rows, setRows] = useState<InfoRow[]>([])
    const [selectedFileName, setSelectedFileName] = useState<string | null>(null)
    const [isDark, setIsDark] = useState(false)
    const [nextRowId, setNextRowId] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [isInitialized, setIsInitialized] = useState(false)

    useEffect(() => {
        if (props.initialData) {
            setName(props.initialData.name || '')
            setPosition(props.initialData.position || '')
            setEmail(props.initialData.email || '')
            setTags(props.initialData.tags || [])
            setCroppedImageUrl(props.initialData.imageUrl)
            setRows(props.initialData.rows || [])

            if (props.initialData.rows && props.initialData.rows.length > 0) {
                const maxRowId = props.initialData.rows.reduce(
                    (max, row) => Math.max(max, row.id),
                    -1,
                )
                setNextRowId(maxRowId + 1)
            }

            setIsInitialized(true)
        } else {
            setIsInitialized(true)
        }
    }, [mode, props.initialData])

    const hasUnsavedChanges = useMemo(() => {
        if (!isInitialized || !props.initialData) {
            return !!(
                name.trim() ||
                position.trim() ||
                email.trim() ||
                rows.length > 0 ||
                croppedImageUrl
            )
        }
        const initial = props.initialData
        return !!(
            name.trim() !== (initial.name || '').trim() ||
            position.trim() !== (initial.position || '').trim() ||
            email.trim() !== (initial.email || '').trim() ||
            JSON.stringify(tags.sort()) !== JSON.stringify((initial.tags || []).sort()) ||
            JSON.stringify(rows) !== JSON.stringify(initial.rows || []) ||
            croppedImageUrl !== initial.imageUrl
        )
    }, [isInitialized, name, position, email, tags, rows, croppedImageUrl, props.initialData])

    useEffect(() => {
        if (!hasUnsavedChanges) {
            return
        }

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            e.preventDefault()
            return ''
        }

        window.addEventListener('beforeunload', handleBeforeUnload)

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [hasUnsavedChanges])

    const playLogo = tags.includes('play')
    const nopeLogo = tags.includes('nope')
    const allianceLogo = tags.includes('alliance')

    const title = `${props.titlePrefix} ${nopeLogo ? props.titleNopeSuffix : props.titlePlaySuffix}`

    const customLines: CustomLineType[] = rows
        .filter((row) => row.title.trim() || row.content.trim())
        .map((row) => ({
            key: row.title,
            value: row.content,
            link: row.isLink ? row.link : undefined,
        }))

    const hasEmptyRequiredFields =
        name.trim() === '' && position.trim() === '' && email.trim() === '' && tags.length === 0

    useEffect(() => {
        return () => {
            if (cropSrc) {
                URL.revokeObjectURL(cropSrc)
            }
        }
    }, [cropSrc])

    const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        if (cropSrc) {
            URL.revokeObjectURL(cropSrc)
        }

        setSelectedFileName(file.name)
        setCropSrc(URL.createObjectURL(file))
        setIsCropModalOpen(true)
        e.target.value = ''
    }

    const handleCropComplete = (result: {
        blob: Blob
        objectUrl: string
        width: number
        height: number
    }) => {
        setCroppedImageUrl(result.objectUrl)
        setIsCropModalOpen(false)

        if (cropSrc) {
            URL.revokeObjectURL(cropSrc)
        }
        setCropSrc(null)
    }

    const handleClearImage = () => {
        if (croppedImageUrl) URL.revokeObjectURL(croppedImageUrl)
        setCroppedImageUrl(undefined)

        if (cropSrc) {
            URL.revokeObjectURL(cropSrc)
        }
        setCropSrc(null)
        setSelectedFileName(null)
    }

    const handleAddRow = () => {
        setRows((prev) => [
            ...prev,
            {
                id: nextRowId,
                title: '',
                content: '',
                link: '',
                isLink: false,
            },
        ])
        setNextRowId(nextRowId + 1)
    }

    const handleSave = async () => {
        if (props.onSave) {
            setIsLoading(true)
            try {
                const signatureFormData: SignatureFormData = {
                    name,
                    position,
                    email,
                    tags,
                    rows,
                    imageUrl: croppedImageUrl,
                    company: props.company || props.initialData?.company,
                }

                const result = props.onSave(signatureFormData)
                if (result instanceof Promise) {
                    await result
                }
            } finally {
                setIsLoading(false)
            }
        }
    }

    const handleCancel = () => {
        if (props.onCancel) {
            props.onCancel()
        }
    }

    const onBack = props.navigateBack ?? (() => {})

    return (
        // Allkiri on e-posti HTML: alati hele, ka tumedas teemas (vt theme.scss).
        <div data-theme="light">
            <div className={classNames(getClassNames(props), 'h-container')}>
                <Button
                    variant="link"
                    text={props.backButtonProps.text}
                    size={props.backButtonProps.size}
                    iconLeft={ArrowLeft}
                    onClick={onBack}
                    className="signature-creation__back-button"
                />
                <div className="signature-creation__title">
                    <h2>{title}</h2>
                </div>
                <div className="signature-creation__form ">
                    {hasEmptyRequiredFields && (
                        <div className="signature-creation__required-field-label">
                            {props.requiredFieldsText}
                        </div>
                    )}
                    <TextField
                        label={props.fields.name.label}
                        isRequired={true}
                        placeholder={props.fields.name.placeholder}
                        size={props.fields.name.size}
                        className="signature-creation__name"
                        value={name}
                        onChange={(value: string) => setName(value)}
                        inputProps={{ maxLength: 128 }}
                    />
                    <TextField
                        label={props.fields.position.label}
                        isRequired={true}
                        placeholder={props.fields.position.placeholder}
                        size={props.fields.position.size}
                        className="signature-creation__position"
                        value={position}
                        onChange={(value: string) => setPosition(value)}
                        inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                        label={props.fields.email.label}
                        isRequired={true}
                        placeholder={props.fields.email.placeholder}
                        size={props.fields.email.size}
                        className="signature-creation__email"
                        value={email}
                        onChange={(value: string) => setEmail(value)}
                        inputProps={{ maxLength: 200 }}
                    />

                    <SignatureInfoRows
                        rows={rows}
                        onRowsChange={setRows}
                        titleFieldProps={props.customRow.titleFieldProps}
                        contentFieldProps={props.customRow.contentFieldProps}
                        linkFieldProps={props.customRow.linkFieldProps}
                        toggleProps={props.toggleProps}
                        removeButtonProps={props.buttons.removeRow}
                        isLinkLabel={
                            props.isLinkLabel ??
                            t.signature.isLink(props.state?.lang ?? DEFAULT_LANGUAGE)
                        }
                    />

                    <Button
                        text={props.buttons.addRow.text}
                        size={props.buttons.addRow.size}
                        variant="primary"
                        className="signature-creation__addrow-button"
                        onClick={handleAddRow}
                    />

                    <Grid wrap={['xs']} gutter="default" className="signature-creation__columns">
                        <GridColumn
                            width={['xs-12', 'md-6']}
                            className="signature-creation__column"
                        >
                            <div>
                                <MultiSelect<LogoTag>
                                    options={logoOptions}
                                    value={tags}
                                    onChange={setTags}
                                    label={props.multiSelectProps.label}
                                    isRequired
                                />
                            </div>
                            <div>
                                <div className="signature-creation__selection-title">
                                    {props.image.uploadLabel}
                                </div>
                                <div className="signature-creation__image-upload">
                                    {croppedImageUrl ? (
                                        <Button
                                            text={props.image.removeButtonProps.text}
                                            size={props.image.removeButtonProps.size}
                                            variant="alert"
                                            onClick={handleClearImage}
                                        />
                                    ) : (
                                        <label className="button button--variant-secondary button--size-small">
                                            <span className="button__text">
                                                {props.image.selectButtonProps.text}
                                            </span>
                                            <input
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                onChange={handleImageSelect}
                                                className="h-visually-hidden"
                                            />
                                        </label>
                                    )}
                                    <span
                                        className={classNames(
                                            'signature-creation__image-filename',
                                            !selectedFileName &&
                                                'signature-creation__image-filename--empty',
                                        )}
                                    >
                                        {selectedFileName ?? props.image.noImageSelectedText}
                                    </span>
                                </div>
                                <CropModal
                                    isOpen={isCropModalOpen}
                                    src={cropSrc}
                                    lang={props.cropModalProps.lang}
                                    onClose={() => {
                                        setIsCropModalOpen(false)
                                        if (cropSrc) {
                                            URL.revokeObjectURL(cropSrc)
                                        }
                                        setCropSrc(null)
                                        setSelectedFileName(null)
                                    }}
                                    onComplete={handleCropComplete}
                                    title={props.cropModalProps.title}
                                />
                            </div>
                        </GridColumn>

                        <GridColumn width={['xs-12', 'md-6']}>
                            <SignaturePreview
                                previewTitle={props.previewTitle}
                                isDark={isDark}
                                onDarkModeChange={setIsDark}
                                darkModeIconOn={Moon}
                                darkModeIconOff={Sun}
                                signatureProps={{
                                    name: name || props.signature.name,
                                    title: position || props.signature.title,
                                    email: email || props.signature.email,
                                    imageUrl: croppedImageUrl || props.signature.imageUrl,
                                    isPlay: true,
                                    isNope: false,
                                    isDark,
                                    playLogo,
                                    nopeLogo,
                                    allianceLogo,
                                    customLines,
                                }}
                            />
                        </GridColumn>
                    </Grid>
                </div>
            </div>
            <div className="signature-creation__container">
                <div className="signature-creation__buttons">
                    <Button
                        text={props.buttons.save.text}
                        variant="primary"
                        size={props.buttons.save.size}
                        onClick={handleSave}
                        isDisabled={isLoading || props.isLoading}
                    />
                    <Button
                        text={props.buttons.cancel.text}
                        variant="alert"
                        size={props.buttons.cancel.size}
                        onClick={handleCancel}
                        isDisabled={isLoading || props.isLoading}
                    />
                </div>
            </div>
        </div>
    )
}

const getClassNames = (props: SignatureCreationProps): string => {
    return classNames('signature-creation', props.className)
}
